import React, { useEffect, useMemo, useState } from 'react';

const formatLocalDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const DateRangePicker = ({
  value,
  onDateRangeChange,
  minDate = 0,
  onClear,
  hasRange,
}) => {
  const [startDate, setStartDate] = useState(value?.start || '');
  const [endDate, setEndDate] = useState(value?.end || '');

  useEffect(() => {
    setStartDate(value?.start || '');
    setEndDate(value?.end || '');
  }, [value?.start, value?.end]);

  const minDateString = useMemo(() => {
    if (typeof minDate !== 'number') return '';
    const today = new Date();
    today.setHours(12, 0, 0, 0); // מונע בעיות UTC
    today.setDate(today.getDate() + minDate);
    return formatLocalDate(today);
  }, [minDate]);

  const isInvalidRange =
    startDate && endDate && new Date(startDate) > new Date(endDate);

  const handleDateChange = () => {
    if (!startDate || !endDate || isInvalidRange) return;
    onDateRangeChange?.(startDate, endDate);
  };

  const handleClear = () => {
    setStartDate('');
    setEndDate('');
    onClear?.();
  };

  return (
    <div className="flex flex-wrap items-end gap-3" dir="rtl">
      <div className="flex flex-col gap-1">
        <label className="text-sm font-medium text-gray-700">מתאריך</label>
        <input
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          min={minDateString}
          dir="ltr"
          className="border border-gray-300 rounded px-2 py-1.5 text-sm text-gray-700 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 w-36"
        />
      </div>

      <div className="flex flex-col gap-1">
        <label className="text-sm font-medium text-gray-700">עד תאריך</label>
        <input
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          min={startDate || minDateString}
          dir="ltr"
          className="border border-gray-300 rounded px-2 py-1.5 text-sm text-gray-700 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 w-36"
        />
      </div>

      <button
        type="button"
        onClick={handleDateChange}
        disabled={!startDate || !endDate || isInvalidRange}
        className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white text-sm font-medium rounded-full transition-colors"
      >
        הצג
      </button>

      {hasRange && (
        <button
          type="button"
          onClick={handleClear}
          className="inline-flex items-center gap-1 text-xs font-semibold text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 rounded-full px-3 py-1.5 transition-colors whitespace-nowrap"
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
          נקה סינון
        </button>
      )}

      {isInvalidRange && (
        <div className="w-full text-xs text-red-600 font-medium">
          תאריך הסיום חייב להיות שווה או מאוחר מתאריך ההתחלה
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;